<template>
  <q-btn-toggle
    v-model="dateSelection"
    class="border-grey-3"
    :class="{ 'q-mx-md': $register, 'q-my-md q-mx-xs': !$register && !activityId }"
    size="md"
    unelevated
    :spread="!!activityId"
    toggle-color="white"
    toggle-text-color="primary"
    color="grey-4"
    text-color="dark"
    :options="dateOptions"
    :disable="disabled"
    @click="dateSelectionClickHandler"
  >
    <template #all>
      {{ $t('collection.all') }}
    </template>
    <template #today>
      {{ $t('time.today') }}
    </template>
    <template #tomorrow>
      {{ $t('time.tomorrow') }}
    </template>
    <template #calendar>
      <div
        ref="custom"
        class="row q-gutter-sm"
      >
        <q-icon
          name="calendar_today"
        />
        <div v-if="customDate">
          {{ customDateFormatted }}
        </div>
        <q-popup-proxy
          v-model="showCalendar"
          transition-show="scale"
          transition-hide="scale"
        >
          <q-date
            v-model="date"
            mask="YYYY-MM-DD"
            no-unset
            :options="formattedDatesWithShowing"
          />
        </q-popup-proxy>
      </div>
    </template>
  </q-btn-toggle>
</template>

<script>
import { date } from 'quasar'
const { formatDate, extractDate } = date

export default {
  name: 'DateToggle',
  configuration: ['currentSite'],
  props: {
    activityId: {
      type: Number,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select-date'],
  data () {
    return {
      today: this.currentSite.today,
      tomorrow: this.currentSite.tomorrow,
      date: this.currentSite.today,
      dateSelection: 'today',
      showCalendar: false,
      customDate: null
    }
  },
  computed: {
    dateOptions () {
      if (!this.datesWithActivities.value) {
        return []
      }

      const dateOptions = []

      if (this.datesWithActivities.value.includes(this.today)) {
        dateOptions.push({ value: 'today', slot: 'today' })
      }

      if (this.datesWithActivities.value.includes(this.tomorrow)) {
        dateOptions.push({ value: 'tomorrow', slot: 'tomorrow' })
      }

      const countDaysOtherThanTodayAndTomorrow = this.datesWithActivities.value.length - dateOptions.length
      if (countDaysOtherThanTodayAndTomorrow > 0) {
        dateOptions.push({ value: 'custom', slot: 'calendar' })
      }

      return dateOptions
    },
    formattedDatesWithShowing () {
      return this.datesWithActivities.value?.map(date => date.replace(/-/g, '/')) || []
    },
    customDateFormatted () {
      return formatDate(extractDate(this.date + ' 00:00', 'YYYY-MM-DD HH:mm'), 'MMM DD')
    }
  },
  watch: {
    date: {
      handler () {
        this.showCalendar = false
        this.validateDateSelection()
      },
      immediate: true
    },
    dateSelection () {
      if (this.dateSelection === 'today') {
        this.date = this.today
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_TODAY)
      } else if (this.dateSelection === 'tomorrow') {
        this.date = this.tomorrow
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_TOMORROW)
      } else if (this.dateSelection === 'custom') {
        this.validateDateSelection()
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_CUSTOM)
      }
    },
    dateOptions () {
      const noActivitySessionsTodayOrTomorrow =
        _(this.dateOptions).map('value').find(value => ['today', 'tomorrow'].includes(value)) === undefined

      if (noActivitySessionsTodayOrTomorrow) {
        this.dateSelection = 'custom'
        this.customDate = true
        this.date = this.datesWithActivities.value?.[0]
      }
    }
  },
  mounted () {
    this.validateDateSelection()
  },
  methods: {
    dateSelectionClickHandler (event) {
      if (!this.$refs.custom) return

      if (
        this.$refs.custom === event.target ||
        this.$refs.custom.contains(event.target) ||
        event.target.contains(this.$refs.custom)) {
        this.showCalendar = true
      }
    },
    validateDateSelection () {
      if (this.date === this.today) {
        this.dateSelection = 'today'
        this.customDate = false
      } else if (this.date === this.tomorrow) {
        this.dateSelection = 'tomorrow'
        this.customDate = false
      } else {
        this.dateSelection = 'custom'
        this.customDate = true
      }

      this.$emit('select-date', this.date)
    }
  },
  models: {
    datesWithActivities: {
      variables () {
        return {
          activity_id: this.activityId
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.border-grey-3 {
  border: 3px solid $grey-4;
}
</style>
